import React from "react";
import IndustryPage from "../components/IndustryPage";
import { Camera, ChefHat, Utensils, BarChart3 } from "lucide-react";

export default function Restaurants({ onRequestDemo }) {
  return (
    <IndustryPage
      onRequestDemo={onRequestDemo}
      pill="RESTAURANTS & HOSPITALITY"
      title="EVERY PLATE."
      titleAccent="EVERY LOCATION. IDENTICAL."
      tagline="AI-POWERED PLATING · PORTION · QUALITY."
      description="Multi-unit operators lose margin and brand consistency to plating drift, portion creep, and ingredient variance. StrainScan Pro sits at the pass, on the line, or on a phone in the manager's hand — grading every plate against the corporate spec in real time, and coaching the line before the ticket ever leaves the kitchen."
      heroImage="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1600&q=85"
      heroLabel="PLATE APPROVED"
      heroScore="97.4%"
      sideStats={[
        { label: "PLATING", value: "IN-SPEC", sub: "97.4% MATCH" },
        { label: "PORTION", value: "6.2 oz", sub: "TARGET 6.0 ±0.5" },
        { label: "TICKET", value: "R-2025-2841", sub: "LOC 12" },
      ]}
      howSteps={[
        { icon: <Camera size={22} />, title: "PASS CAMERA", desc: "A single overhead camera at the pass — or a manager's phone — captures every plate as it leaves the kitchen." },
        { icon: <ChefHat size={22} />, title: "SPEC MATCH", desc: "Compares the plate against your corporate photo spec: garnish placement, sauce coverage, component count." },
        { icon: <Utensils size={22} />, title: "PORTION CHECK", desc: "Estimates protein weight, side portions, and sauce volume from a top-down photo with ±5% accuracy." },
        { icon: <BarChart3 size={22} />, title: "COACH IN REAL-TIME", desc: "Line cooks see instant thumbs-up / red-flag feedback. Managers get roll-up scores per shift, per station." },
      ]}
      capabilities={[
        { title: "PLATING GRADER", desc: "Scores every plate against the corporate photo spec on composition, garnish, sauce coverage, and presentation." },
        { title: "PORTION CONTROL", desc: "Measures protein and side portions from a top-down image — ±5% of scale accuracy, no weighing required." },
        { title: "INGREDIENT AUDIT", desc: "Flags missing components, wrong side, mixed-up modifiers, or incorrect substitutions before the plate leaves." },
        { title: "CROSS-LOCATION KPIS", desc: "Roll-up dashboards for the head of ops: consistency scores by unit, shift, station, and chef." },
        { title: "TRAINING PLAYBACK", desc: "Every off-spec plate becomes a coaching moment with side-by-side spec vs. actual imagery for the daily lineup." },
        { title: "FRAUD & WASTE", desc: "Detects portion creep, unauthorized comps, and off-menu items so ops sees the leaks in the P&L before finance does." },
      ]}
      metrics={[
        { label: "PORTION ACCURACY", value: "±5%", sub: "OF SCALE" },
        { label: "PLATING SPEED", value: "<1s", sub: "PER PHOTO" },
        { label: "COGS SAVINGS", value: "4.8%", sub: "TARGET" },
        { label: "COMPLAINT DROP", value: "62%", sub: "TARGET" },
      ]}
      useCases={[
        { kpi: "4.8%", title: "LOWER FOOD COST", sub: "TARGET", desc: "Designed to drive up to 4.8 percentage-point food cost reduction for multi-unit operators within 90 days of deployment." },
        { kpi: "62%", title: "FEWER COMPLAINTS", sub: "TARGET", desc: "Targeting up to a 62% reduction in guest complaints about portion size, missing items, and presentation." },
        { kpi: "3×", title: "TRAINING ROI", sub: "TARGET", desc: "AI-driven coaching playback is designed to help new line cooks reach corporate plating spec in one-third the time." },
      ]}
      quote={{
        text: "Consistency is the hardest promise in hospitality. Fifty locations should feel like one kitchen. StrainScan Pro exists so that every plate leaving every pass matches the standard the chef designed — no drift, no compromise, no matter the shift.",
        author: "Marshall Perkins",
        role: "Founder & CEO, StrainScan Pro",
      }}
    />
  );
}
